/**
 * Cached loaders for every application view, shared by the preload policy,
 * the boot preload and TabContent, so a view whose code is in renders at once
 * (see platform/lib/cachedModule.ts). Each resolves once the catalog
 * namespaces of its text are in too (i18n/catalogs.ts); `pnpm audit:i18n`
 * checks the lists against the keys the view and its lazy parts use.
 */
import { withCatalogs } from '@/i18n/catalogs';
import { cachedModule } from '@/platform/lib/cachedModule';

export const loadHomeModule = cachedModule(withCatalogs([], () => import('@/app/apps/components')));
export const loadPatientModule = cachedModule(
  withCatalogs(
    [
      'calendar',
      'clinicalTrials',
      'docs',
      'investigator',
      'lists',
      'patients',
      'platform',
      'publicBooking',
      'settings',
      'training',
    ],
    () => import('@/app/patient/components')
  )
);
export const loadClinicalTrialsModule = cachedModule(
  withCatalogs(
    [
      'calendar',
      'chat',
      'clinicalTrials',
      'dashboards',
      'docs',
      'email',
      'investigator',
      'lists',
      'mobile',
      'patients',
      'platform',
      'publicBooking',
      'settings',
      'teamChat',
      'training',
    ],
    () => import('@/app/clinical-trials/components')
  )
);
export const loadSettingsModule = cachedModule(
  withCatalogs(
    [
      'calendar',
      'clinicalTrials',
      'docs',
      'email',
      'feasibility',
      'platform',
      'settings',
      'training',
      'video',
      'voice',
    ],
    () => import('@/app/settings/components')
  )
);
export const loadTemplatesModule = cachedModule(
  withCatalogs(
    [
      'agents',
      'calendar',
      'docs',
      'email',
      'lists',
      'meetingCapture',
      'meetingOutputs',
      'platform',
      'settings',
      'teamChat',
      'templates',
      'training',
      'voice',
    ],
    () => import('@/app/templates/components')
  )
);
export const loadTeamChatModule = cachedModule(
  withCatalogs(
    [
      'calendar',
      'canvas',
      'chat',
      'clinicalTrials',
      'docs',
      'email',
      'feasibility',
      'lists',
      'meetingCapture',
      'meetingOutputs',
      'patients',
      'platform',
      'publicBooking',
      'settings',
      'teamChat',
      'templates',
      'vaultItems',
      'video',
      'voice',
    ],
    () => import('@/app/team-chat/components')
  )
);
export const loadVaultModule = cachedModule(
  withCatalogs(
    ['vault', 'vaultAccount', 'vaultEditor', 'vaultItems'],
    () => import('@/app/vault/components')
  )
);
export const loadVideoModule = cachedModule(
  withCatalogs(
    [
      'calendar',
      'dashboards',
      'docs',
      'lists',
      'platform',
      'publicBooking',
      'settings',
      'training',
      'video',
    ],
    () => import('@/app/video/components/VideoView')
  )
);
export const loadPhoneModule = cachedModule(
  withCatalogs(['phone', 'phoneRuns', 'platform'], () => import('@/app/phone/components'))
);
export const loadCanvasModule = cachedModule(
  withCatalogs(
    [
      'calendar',
      'canvas',
      'chat',
      'clinicalTrials',
      'dashboards',
      'docs',
      'email',
      'investigator',
      'labels',
      'lists',
      'meetingCapture',
      'meetingOutputs',
      'mobile',
      'patients',
      'platform',
      'publicBooking',
      'settings',
      'teamChat',
      'templates',
      'training',
      'vaultItems',
      'video',
      'voice',
    ],
    () => import('@/app/canvas/components')
  )
);
export const loadDashboardsModule = cachedModule(
  withCatalogs(
    [
      'calendar',
      'clinicalTrials',
      'dashboards',
      'docs',
      'investigator',
      'lists',
      'mobile',
      'platform',
      'training',
    ],
    () => import('@/app/dashboards/components')
  )
);
export const loadSigningModule = cachedModule(
  withCatalogs(
    [
      'calendar',
      'docs',
      'email',
      'meetingCapture',
      'meetingOutputs',
      'platform',
      'settings',
      'signing',
      'teamChat',
      'voice',
    ],
    () => import('@/app/signing/SigningView')
  )
);
export const loadFormsModule = cachedModule(
  withCatalogs(
    ['calendar', 'clinicalTrials', 'docs', 'email', 'forms', 'settings', 'training'],
    () => import('@/app/forms/components')
  )
);
export const loadResearchModule = cachedModule(
  withCatalogs(['calendar', 'email', 'research'], () => import('@/app/research/components'))
);
export const loadFeasibilityModule = cachedModule(
  withCatalogs(
    ['clinicalTrials', 'feasibility', 'lists', 'settings'],
    () => import('@/app/feasibility/components')
  )
);
export const loadTrialMatcherModule = cachedModule(
  withCatalogs(
    ['calendar', 'clinicalTrials', 'email', 'training', 'trialMatcher'],
    () => import('@/app/trial-matcher/components')
  )
);
export const loadMarketingModule = cachedModule(
  withCatalogs(['clinicalTrials'], () => import('@/app/marketing/MarketingView'))
);
export const loadTrainingModule = cachedModule(
  withCatalogs(['dashboards', 'training'], () => import('@/app/training/components'))
);
